import { createRef, RefObject, useState } from 'react'

import { useNavigate } from 'react-router-dom'
import { Routes } from 'common/constants'
import { EStatus, TServiceData } from 'common/types'
import { useAppContext } from 'contexts/app-context/hooks/useAppContext'
import { TSupportDetails } from 'contexts/app-context/types/app-context/components/support'
import { useHasScroll } from 'common/hooks/useHasScroll'
import { useScreenFinishLoadTac } from 'common/hooks/useScreenFinishLoadTac'
import { getTagColor, getTagtext } from 'common/services'
import {
  copyToClipboard,
  logBridgeError,
  requestAnalytics,
  sendSelectContent,
} from 'common/helpers'
import { IS_SEND_DOCUMENTS_ENABLED } from 'pages/my-services/utils/constants'

import { getDate, getTrackStatus } from '../services/ServiceDetails.services'
import { TServiceStatus, TTag } from '../types/ServiceDetails.types'

interface TUseServiceDetailsReturn<TService> {
  content: Partial<TSupportDetails>
  service: TService
  tag: TTag
  trackingStatus?: TServiceStatus
  sendDocuments: boolean
  isCompleted: boolean
  isCanceled: boolean
  hasScroll: boolean
  ref: RefObject<HTMLDivElement | null>
  isActive: boolean
  isAppLoading: boolean
  isAppError: boolean
  isAwaitingProtocol: boolean
  isLoadingRating: boolean
  clipboardFeedbackMessage: string | null
  onRateYourService: () => Promise<void>
  onClick: () => Promise<void>
  copyProtocol: () => Promise<void>
  onSendDocuments: () => Promise<void>
}

export function useServiceDetails({
  service,
}: {
  service?: TServiceData
}): TUseServiceDetailsReturn<TServiceData | undefined> {
  const [isLoadingRating, setIsLoadingRating] = useState(false)
  const [clipboardFeedbackMessage, setClipboardFeedbackMessage] = useState<string | null>(null)

  const ref = createRef<HTMLDivElement>()

  const hasScroll = useHasScroll(ref)

  const navigate = useNavigate()

  const { privacyCenterContent, userSelectedEntity, isAppLoading, isAppError } = useAppContext()

  const supportData = privacyCenterContent?.components?.supportData

  const supportDetails = privacyCenterContent?.components?.supportDetails?.content
  const isActive = !!privacyCenterContent?.components?.supportDetails?.isActive

  const trackingInfo = supportDetails?.trackYourRequest

  const serviceStatus = service?.status

  const isCompleted =
    serviceStatus === EStatus.CLOSED.toUpperCase() ||
    serviceStatus === EStatus.RESOLVED.toUpperCase()

  const isCanceled =
    serviceStatus === EStatus.ABANDONED.toUpperCase() ||
    serviceStatus === EStatus.CANCELLED.toUpperCase()

  const isAwaitingProtocol = serviceStatus === EStatus.AWAITING.toUpperCase()

  const sendDocuments = serviceStatus === EStatus.PENDING.toUpperCase() && IS_SEND_DOCUMENTS_ENABLED

  const theme = serviceStatus ? getTagColor(serviceStatus) : 'gray'

  const status = serviceStatus ? getTagtext(supportData?.filterDictionary, serviceStatus) : ''

  const tag: TTag = { theme, status }

  const formattedService = service
    ? {
        ...service,
        openingDate: getDate(new Date(service.openingDate)),
      }
    : undefined

  const trackingStatus = getTrackStatus({
    status: serviceStatus,
    trackingStatus: trackingInfo,
  })

  const content = {
    pageTitle: supportDetails?.pageTitle,
    aboutYourRequest: supportDetails?.aboutYourRequest,
    supportResponse: supportDetails?.supportResponse,
    trackYourRequest: supportDetails?.trackYourRequest,
  }

  const isViewReady =
    !isAppLoading &&
    !isAppError &&
    isActive &&
    !!formattedService &&
    !!trackingStatus &&
    !!content.pageTitle &&
    !!content.aboutYourRequest &&
    !!content.supportResponse &&
    !!content.trackYourRequest

  useScreenFinishLoadTac({
    name: 'ServiceDetails',
    screen: Routes.SERVICE_DETAILS,
    action: 'ServiceDetailsFinishLoad',
    entity: userSelectedEntity,
    shouldSend: isViewReady,
    logLabel: 'send service details finish tac',
  })

  const sendServiceDetailsSelectContent = async (
    elementName: string,
    scenarioParams: Record<string, string> = {},
  ) => {
    try {
      await sendSelectContent({
        flow: 'service_details',
        step: 'service_details',
        screen_name: Routes.SERVICE_DETAILS,
        element_name: elementName,
        element_type: 'button',
        parent_name: 'ServiceDetails',
        scenario: JSON.stringify({
          screen: Routes.SERVICE_DETAILS,
          product: userSelectedEntity,
          ...scenarioParams,
          ref_type: 'button',
          content_action: 'touch',
        }),
      })
    } catch (error: unknown) {
      logBridgeError(`send service details ${elementName} tic`, error)
    }
  }

  return {
    content,
    service: formattedService,
    tag,
    trackingStatus,
    sendDocuments,
    isCompleted,
    isCanceled,
    hasScroll,
    ref,
    isActive,
    isAppLoading,
    isAppError,
    isAwaitingProtocol,
    isLoadingRating,
    clipboardFeedbackMessage,
    onRateYourService: async () => {
      await sendServiceDetailsSelectContent('rateYourService')

      setIsLoadingRating(true)

      try {
        await requestAnalytics('privacy_center_service', {
          show_research: 'true',
        })
      } catch (error: unknown) {
        logBridgeError('request service details analytics', error)
      } finally {
        setIsLoadingRating(false)
      }
    },
    onClick: async () => {
      if (!service) return

      await sendServiceDetailsSelectContent('viewServiceResponse')

      navigate(Routes.SERVICE_RESPONSE, {
        state: {
          response: service?.areaResponse ?? '',
          aditionalInfo: service?.additionalInfo ?? '',
        },
      })
    },
    copyProtocol: async () => {
      if (!service) return

      await sendServiceDetailsSelectContent('copyProtocol', {
        hasProtocol: `${Boolean(service.protocol)}`,
      })

      if (!service.protocol) {
        setClipboardFeedbackMessage('Protocolo indisponivel para copia.')
        return
      }

      try {
        await copyToClipboard('protocol', service.protocol)
        setClipboardFeedbackMessage('Protocolo copiado com sucesso.')
      } catch (error: unknown) {
        logBridgeError('copy service protocol', error)
        setClipboardFeedbackMessage('Nao foi possivel copiar o protocolo.')
      }
    },
    onSendDocuments: async () => {
      if (!service) return

      await sendServiceDetailsSelectContent('sendDocuments')

      navigate(Routes.NEW_SERVICE, { state: { service } })
    },
  }
}
